import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MailCheck,
} from "lucide-react";
import Header from "../components/HomePage/header";
import Footer from "../components/HomePage/footer";

const StatusMessage = ({ isSuccess, message }) => (
  <div
    className={`mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${
      isSuccess
        ? "border-green-200 bg-green-50 text-green-800"
        : "border-red-200 bg-red-50 text-red-800"
    }`}
  >
    {isSuccess ? (
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    ) : (
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
    )}
    <p className="font-semibold">{message}</p>
  </div>
);

const VerifyEmailPage = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Track if API call is already made
  const apiCallMade = useRef(false);

  // Extract token only once
  const token = React.useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("token");
  }, [location.search]);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        if (!apiCallMade.current) {
          setMessage("Verification link is missing or has already been used.");
          setIsSuccess(false);
          setLoading(false);
        }
        return;
      }

      if (apiCallMade.current) {
        return; // Prevent multiple API calls
      }

      apiCallMade.current = true; // Mark API call as made

      try {
        const response = await fetch(`/api/verify-email?token=${token}`, {
          method: "GET",
        });

        if (response.ok) {
          setMessage(
            "Your email has been successfully verified. You will be redirected to login."
          );
          setIsSuccess(true);
          setLoading(false);

          // Clear the token from the URL to prevent further calls
          navigate(window.location.pathname, { replace: true });

          // Redirect after a delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        } else {
          const data = await response.json();
          setMessage(data.message || "Verification failed or token expired.");
          setIsSuccess(false);
          setLoading(false);
        }
      } catch (error) {
        setMessage("There was an error verifying your email. Please try again.");
        setIsSuccess(false);
        setLoading(false);
      }
    };

    verifyEmail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only runs when the token changes

  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Header />

      <main className="px-4 py-10 sm:px-6 lg:px-8">
        <section className="mx-auto grid w-full max-w-6xl gap-8 lg:min-h-[640px] lg:grid-cols-[0.9fr,1.1fr] lg:items-center">
          <div className="max-w-xl">
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Verify your email
            </h1>
            <p className="mt-3 text-sm leading-6 text-gray-500 sm:text-base">
              We are confirming your PrepNexis email so your account can be
              activated securely.
            </p>
            <div className="mt-6 border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-gray-900">
                This usually takes a few seconds.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Keep this page open while we finish the verification.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
            <div className="mb-6 flex items-start gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <MailCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Email verification
                </p>
                <h2 className="mt-1 text-xl font-extrabold text-gray-900 sm:text-2xl">
                  Checking verification link
                </h2>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-[180px] flex-col items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-6 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-purple-700" />
                <p className="mt-4 text-sm font-semibold text-gray-900">
                  Verifying your email
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Please wait while we confirm your link.
                </p>
              </div>
            ) : (
              <>
                <StatusMessage isSuccess={isSuccess} message={message} />

                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    Next step
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    {isSuccess
                      ? "You will be redirected to login shortly."
                      : "Request a fresh verification email from the login page if the link has expired."}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-5">
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 transition hover:text-purple-900"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Back to login
                  </a>
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyEmailPage;
